// Uzbekistan phone number validation
// Format: 998 XX XXX XX XX (without + symbol)
// Both user input and backend: 998XXXXXXXXX

// Phone validation regex for Uzbekistan
// Valid operator codes: 33, 55, 77, 88, 90, 91, 93, 94, 95, 97, 98, 99, 20, 50, 70, 71, 73, 74, 75, 78, 79
// Accepts: +998XXXXXXXXX, 998XXXXXXXXX
export const phoneRegex = /^(\+?998)(33|55|77|88|90|91|93|94|95|97|98|99|20|50|70|71|73|74|75|78|79)\d{7}$/;

type TranslationFunction = (key: string) => string;

/**
 * Validates Uzbekistan phone number
 * Format: 998 XX XXX XX XX (without + for user input)
 * - Must start with 998
 * - Must have valid operator code
 * - Must have 9 digits after country code
 */
export const createPhoneValidator = (t: TranslationFunction) => {
  return (_: unknown, value: string) => {
    if (!value) {
      return Promise.reject(t('user.validation.phoneRequired'));
    }

    // Remove spaces, dashes, and parentheses for validation
    const cleanedValue = value.replace(/[\s\-()]/g, '');

    if (!phoneRegex.test(cleanedValue)) {
      return Promise.reject(t('user.validation.phoneInvalid'));
    }

    return Promise.resolve();
  };
};

/**
 * Formats phone number to 998 XX XXX XX XX format (for display, without +)
 */
export const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const cleaned = value.replace(/\D/g, '');

  // Don't auto-add 998 during formatting - format only what user has typed
  const digits = cleaned;

  // Format: 998 XX XXX XX XX (without +)
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return digits.slice(0, 3) + ' ' + digits.slice(3);
  if (digits.length <= 8) return digits.slice(0, 3) + ' ' + digits.slice(3, 5) + ' ' + digits.slice(5);
  if (digits.length <= 10)
    return digits.slice(0, 3) + ' ' + digits.slice(3, 5) + ' ' + digits.slice(5, 8) + ' ' + digits.slice(8);
  return (
    digits.slice(0, 3) +
    ' ' +
    digits.slice(3, 5) +
    ' ' +
    digits.slice(5, 8) +
    ' ' +
    digits.slice(8, 10) +
    ' ' +
    digits.slice(10, 12)
  );
};

/**
 * Normalizes phone input - allows only digits (no + shown to user)
 */
export const normalizePhoneInput = (value: string): string => {
  if (!value) return '';

  // Keep only digits (remove all non-digit characters including +)
  const result = value.replace(/\D/g, '');

  // Don't auto-add 998 - let user type freely
  // If they start with valid digits, assume they're typing 998 manually
  // Only enforce 998 on validation, not during input

  // Limit length to 12 characters (998XXXXXXXXX without +)
  return result.slice(0, 12);
};

/**
 * Prepares phone number for backend submission
 * Ensures phone is always in +998XXXXXXXXX format
 */
export const preparePhoneForSubmit = (value: string): string => {
  if (!value) return '';

  // Remove all non-digit characters
  const cleaned = value.replace(/\D/g, '');

  // Ensure it starts with 998, then add +
  if (cleaned.startsWith('998')) {
    return '+' + cleaned;
  }

  // If doesn't start with country code, add +998
  return '+998' + cleaned;
};
